import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api.ts';
import { cn, plural } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { Button, Checkbox, Dialog, Field, Input, Select, useToast } from '@/components/ui.tsx';
import {
  inputDefault,
  splitShipped,
  type DeployCommit,
  type DeployPlan,
  type WorkflowInput,
} from '@shared/deployPlan.ts';

type Values = Record<string, string | boolean>;

/**
 * Run the production workflow, having first said what it will ship.
 *
 * Every input the workflow declares is offered, read from the workflow file —
 * nothing here knows a particular repository's inputs. The one input this
 * dialog understands is the project's bypass input (Settings → Deploys): it
 * is drawn as the incident switch, and flipping it re-splits the list, since
 * on it everything merged ships rather than only what the gate verified.
 */
export function DeployDialog({
  projectId,
  open,
  onOpenChange,
  canDeploy,
  onNavigate,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canDeploy: boolean;
  /** Closes the mobile drawer the dialog was opened from. */
  onNavigate?: () => void;
}) {
  const { notify } = useToast();
  const [repoId, setRepoId] = useState<string | null>(null);
  const [plan, setPlan] = useState<DeployPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Values>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A repository picked last time may have lost the workflow since, and the
    // picker only shows once a plan loads — so every opening starts from the
    // default repository.
    if (!open) {
      setRepoId(null);
      return;
    }
    let cancelled = false;
    setPlan(null);
    setError(null);
    void api
      .deployPlan(projectId, repoId)
      .then((next) => {
        if (cancelled) return;
        setPlan(next);
        setValues(Object.fromEntries(next.inputs.map((input) => [input.name, inputDefault(input)])));
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [open, projectId, repoId]);

  const bypassName = plan?.gate.bypass_input ?? null;
  const bypass = bypassName ? values[bypassName] === true : false;
  const { shipped, waiting } = useMemo(
    () => splitShipped(plan?.commits ?? [], { gated: Boolean(plan?.gate.check), bypass }),
    [plan, bypass],
  );

  const deploy = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      // An empty text input is left out, so GitHub applies its own default —
      // sent as '', an optional number input would be refused.
      const inputs = Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
      await api.deploy(projectId, { repo_id: plan.repo.id, inputs });
      notify(`${plan.workflow.name} started`, {
        body: `${bypass ? 'Incident deploy — everything merged ships. ' : ''}The Production line follows the run.`,
      });
      onOpenChange(false);
    } catch (e) {
      notify('Could not start the deploy', { body: (e as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={plan ? `Deploy — ${plan.workflow.name}` : 'Deploy'}
      description={
        plan ? (
          <>
            Runs{' '}
            <a href={plan.workflow.runs_url} target="_blank" rel="noreferrer" className="text-accent">
              {plan.workflow.name}
            </a>{' '}
            on {plan.repo.owner}/{plan.repo.name}@{plan.repo.default_branch}.
          </>
        ) : undefined
      }
      footer={
        <>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            tone={bypass ? 'danger' : 'primary'}
            disabled={!plan || busy || !canDeploy}
            onClick={() => void deploy()}
          >
            {bypass ? 'Deploy everything' : 'Deploy'}
          </Button>
        </>
      }
    >
      {error ? <p className="text-[13px] text-warnx">{error}</p> : null}
      {!plan && !error ? <p className="text-[13px] text-mute">Reading the workflow and what is live…</p> : null}

      {plan ? (
        <>
          {plan.targets.length > 1 ? (
            <Field label="Repository">
              <Select
                value={plan.repo.id}
                onChange={setRepoId}
                options={plan.targets.map((t) => ({ value: t.id, label: t.full_name }))}
              />
            </Field>
          ) : null}

          {plan.warnings.map((warning) => (
            <p key={warning} className="text-[13px] text-amberx">
              {warning}
            </p>
          ))}

          {plan.inputs.length > 0 ? (
            <div className="flex flex-col gap-3">
              {plan.inputs.map((input) => (
                <InputControl
                  key={input.name}
                  input={input}
                  incident={input.name === bypassName}
                  value={values[input.name] ?? inputDefault(input)}
                  onChange={(value) => setValues((prev) => ({ ...prev, [input.name]: value }))}
                />
              ))}
            </div>
          ) : null}

          <Contents
            plan={plan}
            shipped={shipped}
            waiting={waiting}
            bypass={bypass}
            onOpenTask={(id) => {
              onOpenChange(false);
              navigate(`/t/${id}`);
              onNavigate?.();
            }}
          />
          {!canDeploy ? <p className="text-[12.5px] text-mute">Only an admin can deploy.</p> : null}
        </>
      ) : null}
    </Dialog>
  );
}

function InputControl({
  input,
  incident,
  value,
  onChange,
}: {
  input: WorkflowInput;
  incident: boolean;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
}) {
  if (input.type === 'boolean') {
    return (
      <div
        className={cn(
          'rounded-[10px] border px-3 py-2.5',
          incident && value === true ? 'border-warnx/40 bg-warnx/8' : 'border-line',
        )}
      >
        <Checkbox
          id={`deploy-input-${input.name}`}
          checked={value === true}
          onChange={onChange}
          label={
            <span className="font-semibold text-t1">
              {incident ? 'Incident' : input.name}
              {incident ? <span className="ml-1.5 font-mono text-[11.5px] font-normal text-mute">{input.name}</span> : null}
            </span>
          }
        />
        {input.description ? (
          <p className="mt-1 pl-[23px] text-[12px] leading-relaxed text-faint">{input.description}</p>
        ) : null}
      </div>
    );
  }

  const hint = input.description ?? undefined;
  const label = `${input.name}${input.required ? ' *' : ''}`;
  if (input.type === 'choice') {
    return (
      <Field label={label} hint={hint}>
        <Select
          value={String(value)}
          onChange={onChange}
          options={input.options.map((option) => ({ value: option, label: option }))}
        />
      </Field>
    );
  }
  return (
    <Field label={label} hint={hint}>
      <Input
        value={String(value)}
        inputMode={input.type === 'number' ? 'decimal' : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function Contents({
  plan,
  shipped,
  waiting,
  bypass,
  onOpenTask,
}: {
  plan: DeployPlan;
  shipped: DeployCommit[];
  waiting: DeployCommit[];
  bypass: boolean;
  onOpenTask: (id: string) => void;
}) {
  if (!plan.live) {
    return <p className="text-[13px] text-mute">{plan.live_error}</p>;
  }
  const liveLine = (
    <p className="text-[12.5px] text-mute">
      Production runs{' '}
      <span className="font-mono">{plan.live.sha.slice(0, 7)}</span>
      {plan.live.on_branch ? '' : ` — not on ${plan.repo.default_branch}, so everything since its date is listed`}.
    </p>
  );
  if (plan.commits.length === 0) {
    return (
      <>
        {liveLine}
        <p className="text-[13px] text-body">Nothing has merged since — this deploy ships what is already live.</p>
      </>
    );
  }

  const gated = Boolean(plan.gate.check) && !bypass;
  return (
    <>
      {liveLine}
      <CommitList
        title={
          shipped.length === 0
            ? `Ships nothing new — no commit since production has passed ${plan.gate.check}`
            : gated
              ? `Ships — passed ${plan.gate.check}`
              : bypass
                ? 'Ships — everything merged'
                : 'Ships'
        }
        commits={shipped}
        onOpenTask={onOpenTask}
      />
      {waiting.length > 0 ? (
        <CommitList
          title={`Waits for ${plan.gate.check}${plan.gate.bypass_input ? ' — or turn on Incident' : ''}`}
          commits={waiting}
          muted
          onOpenTask={onOpenTask}
        />
      ) : null}
    </>
  );
}

/** Tasks first, then the commits that carry no task, collapsed to a count. */
function CommitList({
  title,
  commits,
  muted,
  onOpenTask,
}: {
  title: string;
  commits: DeployCommit[];
  muted?: boolean;
  onOpenTask: (id: string) => void;
}) {
  // A task with two pull requests in the range is listed once.
  const tasks = [...new Map(commits.flatMap((commit) => commit.tasks).map((task) => [task.id, task])).values()];
  const bare = commits.filter((commit) => commit.tasks.length === 0);
  const [showBare, setShowBare] = useState(false);

  return (
    <section>
      <div className="mb-1.5 text-[11.5px] font-semibold text-mute">
        {title}
        {commits.length > 0 ? (
          <span className="font-normal">
            {' '}
            · {plural(tasks.length, 'task')}, {plural(commits.length, 'commit')}
          </span>
        ) : null}
      </div>
      <ul className={cn('flex flex-col gap-0.5', muted && 'opacity-60')}>
        {tasks.map((task) => (
          <li key={task.id}>
            <button
              onClick={() => onOpenTask(task.id)}
              className="rg-focus flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-navhover"
            >
              <span className="min-w-0 flex-1 truncate text-[13px] text-t1">{task.title}</span>
              <span className="shrink-0 text-[11.5px] text-mute">{task.status}</span>
            </button>
          </li>
        ))}
        {bare.length > 0 ? (
          <li>
            <button
              onClick={() => setShowBare((v) => !v)}
              className="rg-focus rounded-md px-2 py-1 text-left text-[12px] text-mute hover:text-hi"
            >
              {showBare ? '▾' : '▸'} {plural(bare.length, 'commit')} with no task
            </button>
          </li>
        ) : null}
        {showBare
          ? bare.map((commit) => (
              <li key={commit.sha}>
                <a
                  href={commit.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-2 rounded-md px-2 py-1 pl-6 text-[12.5px] text-body no-underline hover:bg-navhover"
                >
                  <span className="shrink-0 font-mono text-faint">{commit.sha.slice(0, 7)}</span>
                  <span className="min-w-0 truncate">{commit.headline}</span>
                </a>
              </li>
            ))
          : null}
      </ul>
    </section>
  );
}
